/**
 * The social feature on profiles and lists (2026-10-29):
 * - "Playing X" with the elapsed time on the profile (`ProfileActivity`),
 *   and as the status line of the member list, the DM list and friends
 *   (`useStatusLine`), as Discord: a custom status first, else the game;
 * - your private note on the profile (`ProfileNote`): saved on blur, 256
 *   characters, only you see it.
 */
import { formatElapsed, USER_NOTE_MAX_LENGTH, type Snowflake } from '@neosocial/protocol';
import { useEffect, useRef, useState } from 'react';
import { tr, useT } from '../i18n';
import { flagOn, useFlag } from '../store/config';
import { useData } from '../store/data';
import { customStatusOf, isDeletedUser } from '../store/selectors';
import { Icon } from '../ui/kit';
import { toast, type MenuItem } from '../ui/overlay';
import { playingOfUser } from './activity';
import { focusNoteOf, loadNote, saveNote, useNotes } from './notes';

/** The game someone plays, as others see it (name and start), or null. */
export function usePlaying(
  userId: Snowflake | undefined,
): { name: string; start: number | null } | null {
  // the console's kill switch hides games at once, before presences change
  const on = useFlag('rich_presence');
  const name = useData((d) => (userId ? (playingOfUser(d, userId)?.name ?? null) : null));
  const start = useData((d) => (userId ? (playingOfUser(d, userId)?.start ?? null) : null));
  return on && name ? { name, start } : null;
}

/**
 * The line under a name: the custom status when there is one, else
 * "Playing X" (Discord's order), else null.
 */
export function useStatusLine(userId: Snowflake | undefined): string | null {
  const { t } = useT();
  const custom = useData((d) => (userId ? customStatusOf(d, userId) : null));
  const game = usePlaying(userId)?.name ?? null;
  if (custom) return custom;
  return game ? t('nxs.act.playing', { game }) : null;
}

/** Re-renders every second while mounted (the profile's elapsed time). */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}

/** "Playing a game": the game and how long they have played (the profile card). */
export function ProfileActivity({ userId }: { userId: Snowflake }) {
  const { t } = useT();
  const game = usePlaying(userId);
  const now = useNow(!!game?.start);
  if (!game) return null;
  return (
    <section className="nxs-act" data-prof-activity="">
      <h3>{t('nxs.act.playingTitle')}</h3>
      <div className="nxs-act-card">
        <span className="nxs-act-ic" aria-hidden="true">
          <Icon name="play" />
        </span>
        <span className="nxs-act-t">
          <b dir="auto">{game.name}</b>
          {game.start ? (
            <small>{t('nxs.act.elapsed', { time: formatElapsed(now - game.start) })}</small>
          ) : null}
        </span>
      </div>
    </section>
  );
}

/** Your private note on their profile (Discord's "Note"): click to write, saved when you leave the field. */
export function ProfileNote({ userId }: { userId: Snowflake }) {
  const { t } = useT();
  const on = useFlag('user_notes');
  const saved = useNotes((s) => s.notes[userId]);
  const focus = useNotes((s) => s.focus === userId);
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (on) void loadNote(userId);
  }, [on, userId]);
  // "Add note": the field takes the focus in the frame the card opens, ahead
  // of the card's own first focus (asked for after this one, and it leaves a
  // focus already inside), so what is typed next is the note, never a chat
  // message or the Message button (V-01). It can take the focus while the
  // note still loads: read-only until then.
  useEffect(() => {
    if (!focus) return;
    focusNoteOf(null);
    requestAnimationFrame(() => ref.current?.focus({ preventScroll: true }));
  }, [focus]);
  if (!on) return null;
  const loading = saved === undefined;
  const value = draft ?? saved ?? '';
  const commit = async () => {
    if (draft === null) return;
    const ok = await saveNote(userId, draft);
    setDraft(null);
    if (!ok) {
      const tooLong = [...draft.trim()].length > USER_NOTE_MAX_LENGTH;
      toast(t(tooLong ? 'nxs.note.tooLong' : 'nxs.note.failed'), {
        tone: 'danger',
        icon: 'alert',
      });
    }
  };
  return (
    <section className="nxs-note">
      <h3>
        <label htmlFor={`nxs-note-${userId}`}>{t('nxs.note.title')}</label>
      </h3>
      <textarea
        ref={ref}
        id={`nxs-note-${userId}`}
        className="nxs-note-in"
        dir="auto"
        rows={2}
        maxLength={USER_NOTE_MAX_LENGTH}
        placeholder={t('nxs.note.placeholder')}
        aria-describedby={`nxs-note-only-${userId}`}
        title={t('nxs.note.only')}
        readOnly={loading}
        aria-busy={loading || undefined}
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          // Enter saves (Shift+Enter is a new line), Escape puts it back
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!loading) e.currentTarget.blur();
          } else if (e.key === 'Escape' && draft !== null) {
            e.stopPropagation();
            setDraft(null);
          }
        }}
      />
      <small id={`nxs-note-only-${userId}`} className="nxs-note-only">
        {t('nxs.note.only')}
      </small>
    </section>
  );
}

/**
 * Discord's "Add Note" (or "Edit Note") for someone's menu: `openProfile`
 * opens their profile, whose note field then takes the focus. None for
 * yourself, a deleted account, or while the console's `user_notes` is off.
 */
export function noteMenuItems(userId: Snowflake, openProfile: () => void): MenuItem[] {
  const d = useData.getState();
  const u = d.users[userId];
  if (!flagOn('user_notes') || userId === d.me?.id || !u || isDeletedUser(u)) return [];
  const { t } = tr();
  const has = !!useNotes.getState().notes[userId];
  return [
    {
      label: t(has ? 'nxs.note.edit' : 'nxs.note.add'),
      icon: 'edit',
      run: () => {
        focusNoteOf(userId);
        openProfile();
      },
    },
  ];
}
